import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { TriangleAlert } from 'lucide-react'
import { GameType } from '@/modules/promotion/type'
import { useDateFilter } from '@/common/hooks/useDateFilter'

import { Button } from '@/common/ui/button'
import { RtpConfirmDialog } from '@/modules/promotion/components/RtpConfirmDialog'
import {
	CommissionFormValues,
	commissionFormSchema,
} from './components/FormSchema'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { RadioGroup, RadioGroupItem } from '@/common/ui/radio-group'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import { DateTimePicker } from '@/common/components/DateTimePicker'
import { GameTypeCreditSettings } from '../GameTypeCreditSettings'
import { NameField } from '../CommonField'
import {
	WalletConditionSection,
	prepareWalletConditionForSubmit,
} from '@/common/components/WalletConditionSection'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

interface CommissionFormProps {
	defaultValues?: Partial<CommissionFormValues>
	onSubmit: (data: any) => void
	mode: 'create' | 'edit'
}

const GAME_TYPES = [
	GameType.SLOT,
	GameType.CASINO,
	GameType.LOTTO,
	GameType.SPORT,
	GameType.FISHING,
] as const

export default function CommissionForm({
	defaultValues,
	onSubmit,
	mode,
}: CommissionFormProps) {
	const { dateFilter } = useDateFilter()
	const navigate = useNavigate()
	const [showRtpConfirm, setShowRtpConfirm] = useState(false)
	const [pendingPayload, setPendingPayload] = useState<Record<
		string,
		unknown
	> | null>(null)

	const form = useForm<CommissionFormValues>({
		resolver: zodResolver(commissionFormSchema),
		defaultValues: {
			name: '',
			isActive: true,
			creditRatio: GAME_TYPES.reduce(
				(acc, type) => ({
					...acc,
					[type]: {
						creditRatio: undefined,
						maxCreditAmount: undefined,
					},
				}),
				{} as Record<
					GameType,
					{ creditRatio: number; maxCreditAmount: number }
				>,
			),
			hasExpiration: false,
			multiplier: 1,
			multiplierStartDate: undefined,
			multiplierEndDate: undefined,
			walletCondition: {
				minimumWithdrawCredit: undefined,
				maximumWithdrawCredit: undefined,
				turnoverRound: undefined,
				gameTypes: [],
				whitelistGameProviders: [],
				usePool: false,
				poolPresetConfigs: [],
				presetConfigs: [],
				pgHard: false,
				jokerHard: false,
				jiliHard: false,
				ppHard: false,
				nlcHard: false,
			},
			...defaultValues,
		},
	})

	const handleSubmit = (values: CommissionFormValues) => {
		const { hasExpiration, walletCondition, ...rest } = values

		const payload = {
			...rest,
			startDate: hasExpiration ? dateFilter.startDate : null,
			endDate: hasExpiration ? dateFilter.endDate : null,
			walletCondition: prepareWalletConditionForSubmit(walletCondition),
			// TODO: remove this after backend is updated
			turnoverRound: 1,
		}

		if (mode === 'create') {
			setPendingPayload(payload)
		} else {
			onSubmit(payload)
		}
	}

	return (
		<FormProvider {...form}>
			{mode === 'edit' && (
				<div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
					<TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
					<div className="text-amber-800 dark:text-amber-200">
						<p className="font-medium">
							การแก้ไขนี้จะมีผลกับแรงค์ที่ใช้คอมมิชชั่นนี้อยู่เท่านั้น
						</p>
						<p className="mt-0.5">
							กรุณาตรวจสอบให้แน่ใจว่าการเปลี่ยนแปลงนี้ถูกต้องสำหรับทุกแรงค์ที่ใช้งานอยู่
						</p>
					</div>
				</div>
			)}
			<form
				onSubmit={(e) => {
					e.preventDefault()
					const result = form.handleSubmit((values) => {
						handleSubmit(values)
					}, showFormValidationToast)
					result(e)
				}}
				className="space-y-8"
			>
				{/* Basic Information and Time Period Section */}
				<div className="grid grid-cols-2 gap-4">
					{/* Basic Information Section */}
					<div className="space-y-6 rounded-lg border p-6">
						<h2 className="text-xl font-semibold">ข้อมูลทั่วไป</h2>
						<div className="space-y-4">
							<NameField control={form.control} label="ตั้งชื่อคอมมิชชั่น" />
						</div>
					</div>

					{/* Time Period Section */}
					<div className="space-y-6 rounded-lg border p-6">
						<h2 className="text-xl font-semibold">ระยะเวลา</h2>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="hasExpiration"
								render={({ field }) => (
									<FormItem className="space-y-3">
										<FormLabel>ระยะเวลาคอมมิชชั่น</FormLabel>
										<FormControl>
											<RadioGroup
												onValueChange={(value) =>
													field.onChange(value === 'true')
												}
												defaultValue={field.value ? 'true' : 'false'}
												className="flex flex-col space-y-1"
											>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="false" />
													</FormControl>
													<FormLabel className="font-normal">
														ไม่มีกำหนด
													</FormLabel>
												</FormItem>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="true" />
													</FormControl>
													<FormLabel className="font-normal">
														เลือกระยะเวลา
													</FormLabel>
												</FormItem>
											</RadioGroup>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{form.watch('hasExpiration') && <DateRangePicker />}
						</div>
					</div>
				</div>

				{/* Multiplier Settings Section */}
				<div className="rounded-lg border p-6">
					<div className="mb-6">
						<h2 className="text-xl font-semibold">การตั้งค่าตัวคูณ</h2>
						<p className="mt-2 text-sm text-muted-foreground">
							สร้างอีเว้นท์พิเศษเพื่อเพิ่มอัตราการจ่ายคอมมิชชั่นในช่วงเวลาที่กำหนด
						</p>
					</div>

					{/* Explanation Card */}
					<div className="mb-6 rounded-md bg-blue-50 p-4 dark:bg-blue-950/30">
						<h3 className="mb-2 font-medium text-blue-900 dark:text-blue-100">
							💡 ตัวอย่างการใช้งาน
						</h3>
						<ul className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
							<li>
								<strong>ตัวคูณ 2x:</strong> เช่นค่าคอมมิชชั่นปกติคือ 5%
								จะกลายเป็น 10% ในช่วงเวลาที่กำหนด
							</li>

							<li>
								<strong>ช่วงเทศกาล:</strong> ตั้งค่าตัวคูณ 2x
								ในช่วงวันหยุดยาวเพื่อเพิ่มยอดเล่น
							</li>
						</ul>
					</div>

					<div className="space-y-4">
						<FormField
							control={form.control}
							name="multiplier"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ตัวคูณ (ค่าเริ่มต้น: 1)</FormLabel>
									<FormControl>
										<Input
											type="number"
											placeholder="กรอกตัวคูณ (ขั้นต่ำ 1)"
											{...field}
											value={field.value ?? ''}
											onChange={(e) => {
												const value = e.target.value
												field.onChange(value === '' ? null : Number(value))
											}}
											onBlur={(e) => {
												const value = e.target.value
												if (value === '' || value === null) {
													field.onChange(1)
												}
											}}
										/>
									</FormControl>
									<p className="mt-1 text-xs text-muted-foreground">
										ระบุตัวเลขที่ต้องการคูณกับอัตราคอมมิชชั่นปกติ (เช่น 1.5 =
										เพิ่มเป็น 1.5 เท่า, 2 = เพิ่มเป็น 2 เท่า)
									</p>
									<FormMessage />
								</FormItem>
							)}
						/>

						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="multiplierStartDate"
								render={({ field }) => (
									<FormItem className="flex flex-col">
										<FormLabel>วันเริ่มต้นตัวคูณ</FormLabel>
										<FormControl>
											<DateTimePicker
												date={field.value ? new Date(field.value) : undefined}
												onDateSelect={(date: Date | undefined) => {
													field.onChange(date ? date.toISOString() : null)
												}}
											/>
										</FormControl>

										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="multiplierEndDate"
								render={({ field }) => (
									<FormItem className="flex flex-col">
										<FormLabel>วันสิ้นสุดตัวคูณ</FormLabel>
										<FormControl>
											<DateTimePicker
												date={field.value ? new Date(field.value) : undefined}
												onDateSelect={(date: Date | undefined) => {
													field.onChange(date ? date.toISOString() : null)
												}}
											/>
										</FormControl>

										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>

				{/* Game Type Credit Settings Section */}
				<div className="space-y-6 rounded-lg border p-6">
					<h2 className="text-xl font-semibold">
						การตั้งค่าเครดิตตามประเภทเกม
					</h2>
					<GameTypeCreditSettings control={form.control} name="creditRatio" />
				</div>

				{/* Wallet Condition Section */}
				<WalletConditionSection<CommissionFormValues>
					control={form.control}
					fieldPrefix="walletCondition"
					showGameSettings={true}
					showPoolPreset={true}
				/>

				{/* Action Buttons */}
				<div className="flex items-center gap-4 pb-8">
					<Button
						type="button"
						variant="default"
						className="w-full"
						onClick={() => {
							form.handleSubmit(
								() => setShowRtpConfirm(true),
								showFormValidationToast,
							)()
						}}
					>
						{mode === 'create' ? 'สร้างคอมมิชชั่น' : 'แก้ไขคอมมิชชั่น'}
					</Button>
					<Button
						type="button"
						variant="outline"
						className="w-full"
						onClick={() => navigate(-1)}
					>
						ยกเลิก
					</Button>
				</div>
			</form>
			<RtpConfirmDialog
				open={showRtpConfirm}
				onOpenChange={setShowRtpConfirm}
				onConfirm={() => {
					setShowRtpConfirm(false)
					form.handleSubmit(
						(values) => handleSubmit(values),
						showFormValidationToast,
					)()
				}}
				confirmLabel={mode === 'create' ? 'สร้างคอมมิชชั่น' : 'แก้ไขคอมมิชชั่น'}
			/>
			<AlertDialog
				open={pendingPayload !== null}
				onOpenChange={(open) => {
					if (!open) setPendingPayload(null)
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันการสร้างคอมมิชชั่น</AlertDialogTitle>
						<AlertDialogDescription className="space-y-2">
							<span className="block">
								รายการคอมมิชชั่นที่สร้างใหม่จะมีผลต่อผู้เล่นเมื่อถูกเชื่อมกับแรงค์ที่ต้องการแล้วเท่านั้น
							</span>
							<span className="block font-medium text-foreground">
								หลังจากสร้างแล้ว อย่าลืมไปที่หน้า <strong>จัดการแรงค์</strong>{' '}
								เพื่อเชื่อมรายการคอมมิชชั่นนี้ให้กับแรงค์ที่ต้องการ
							</span>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setPendingPayload(null)}>
							กลับไปแก้ไข
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (pendingPayload) {
									onSubmit(pendingPayload)
									setPendingPayload(null)
								}
							}}
						>
							สร้างคอมมิชชั่น
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</FormProvider>
	)
}
